/**
 * CG-3 — useGuardianApprovals hook (coach-guardian phase).
 *
 * Backs the /approvals inbox: lists the caller's pending guardian
 * approvals (across every athlete they actively guard) and submits
 * approve/decline decisions. Server-authoritative: the guardian_approvals_v1
 * flag gates everything server-side (enabled=false → page renders nothing),
 * and an approve only changes the athlete's plan if the held proposal still
 * passes the full reflow re-verification (fail-closed).
 *
 * CG-4 (v1.1) — also loads the decided history (`?include=decided`, last 10
 * decided items) for the inbox's "Recent" list (design A6).
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';

export interface GuardianApproval {
  id: string;
  athleteUserId: string;
  athleteName: string | null;
  /** Type-driven card content — 'reflow_apply' today; 'plan_review' is the
   *  queued coach tenant (Pass 3). Unknown types render the generic card. */
  approvalType: string;
  summary: {
    weekNumber?: number;
    daysChanged?: number | null;
    /** Pass 3 (plan_review): true when the athlete explicitly escalated. */
    escalated?: boolean;
    /** CG-8 (plan_review_batch): "Weeks 5 to 8" · "Weeks 3, 5 & 9" ·
     *  "4 drafted weeks" — `batchWeekLabel` in hooks/useCoachPlanReviews,
     *  which says the word since the founder ruled register on this pair
     *  (2026-09-09). A grep for the old "Weeks 5–8" finds nothing. */
    weekLabelText?: string;
    /** CG-8 (plan_review_batch): plans collapsed into this card. */
    planCount?: number;
  };
  status?: 'pending' | 'approved' | 'declined' | 'expired' | 'approving';
  createdAt: string;
  decidedAt?: string | null;
}

export interface UseGuardianApprovalsResult {
  enabled: boolean;
  approvals: GuardianApproval[];
  /** Last 10 decided items, newest first (design A6). */
  decided: GuardianApproval[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Resolves with the decision outcome ('approved' | 'declined' | 'expired' | 'already_applied'). */
  decide: (id: string, decision: 'approve' | 'decline') => Promise<string>;
  /** Ruling 25 (CD 2026-08-04) — cancels a started under-13 deletion. Resolves
   *  with the server-stamped status ('declined' renders the `Kept` row). */
  cancelUnder13Deletion: (id: string) => Promise<string>;
  /** B192 — opens a guardian-initiated under-13 deletion request against the
   *  real `POST /api/account/under13-deletion`. Resolves with the approval id
   *  and the earliest completion date (an already-pending request resolves
   *  the SAME way, `alreadyPending: true`, rather than throwing). Throws an
   *  `Error` carrying the server's machine `code` on refusal. */
  requestUnder13Deletion: (athleteUserId: string) => Promise<RequestUnder13DeletionResult>;
}

export interface RequestUnder13DeletionResult {
  approvalId: string;
  coolingOffDays: number;
  earliestExecuteAt: string | null;
  alreadyPending: boolean;
}

export function useGuardianApprovals(): UseGuardianApprovalsResult {
  const { session } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const [enabled, setEnabled] = useState(false);
  const [approvals, setApprovals] = useState<GuardianApproval[]>([]);
  const [decided, setDecided] = useState<GuardianApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAuthed('/api/guardian/approvals?include=decided');
      if (!res.ok) throw new Error(`Failed to load approvals (${res.status})`);
      const json = (await res.json()) as {
        enabled?: boolean;
        approvals?: GuardianApproval[];
        decided?: GuardianApproval[];
      };
      setEnabled(json.enabled === true);
      setApprovals(Array.isArray(json.approvals) ? json.approvals : []);
      setDecided(Array.isArray(json.decided) ? json.decided : []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [session, fetchAuthed]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const decide = useCallback(
    async (id: string, decision: 'approve' | 'decline'): Promise<string> => {
      const res = await fetchAuthed(
        `/api/guardian/approvals/${encodeURIComponent(id)}/decide`,
        { method: 'POST', body: JSON.stringify({ decision }) },
      );
      const json = (await res.json().catch(() => ({}))) as {
        status?: string;
        error?: string;
        code?: string;
      };
      // B216-fix1 (UX finding 1) — a 409 `deletion_in_progress` (the B216
      // single-flight claim: another door is already removing this same
      // athlete's account) is a FAILURE, never an applied outcome. It carries
      // no `status` field (unlike the two pre-existing 409s below, `expired`
      // and "Already decided", which do), so falling through to
      // `json.status || 'approved'` synthesized a false "Deletion started"
      // outcome for an under-13 deletion the guardian never actually
      // started. Thrown here, like any other real failure, so the caller's
      // existing `actionError` path renders the server's own sentence.
      if (res.status === 409 && json.code === 'deletion_in_progress') {
        throw new Error(json.error || 'This account is already being removed.');
      }
      // The remaining 409s carry a terminal status (expired / already_applied
      // / already decided) — the item leaves the pending list either way.
      if (!res.ok && res.status !== 409) {
        throw new Error(json.error || `Decision failed (${res.status})`);
      }
      await refetch();
      return json.status || (decision === 'approve' ? 'approved' : 'declined');
    },
    [fetchAuthed, refetch],
  );

  /**
   * ⚖️ CD 2026-08-04 ruling 25 / S12 — the cancel HALF of the under-13 tenant.
   * The started-state card promises "You can cancel any time before then."; S12
   * says a copy promise of an available action is a CONTROL IN THE SAME VIEW,
   * and this is that control's wire. One press, no confirm — a cancel is the
   * protective act, and friction guards destruction, not protection.
   * Resolves to the status the server stamped ('declined' → the `Kept` row).
   */
  const cancelUnder13Deletion = useCallback(
    async (id: string): Promise<string> => {
      const res = await fetchAuthed(
        `/api/account/under13-deletion/${encodeURIComponent(id)}/cancel`,
        { method: 'POST' },
      );
      const json = (await res.json().catch(() => ({}))) as {
        status?: string;
        error?: string;
      };
      // A 409 carries a terminal status (already decided) — the row resolves
      // either way; only a real failure throws.
      if (!res.ok && res.status !== 409) {
        throw new Error(json.error || `Cancel failed (${res.status})`);
      }
      await refetch();
      return json.status || 'declined';
    },
    [fetchAuthed, refetch],
  );

  /**
   * B192 (security review harvest b190, HIGH finding 1) — the guardian-home
   * caller `handleRequestUnder13Deletion` was missing entirely; this is it.
   * An already-pending request resolves the SAME success shape
   * (`alreadyPending: true`) rather than throwing, so the caller renders one
   * success path either way. Every other refusal throws an `Error` carrying
   * the server's machine `code`, so a caller can choose which few codes are
   * safe to show verbatim (Apple 5.1.1(v): no email address in this copy).
   */
  const requestUnder13Deletion = useCallback(
    async (athleteUserId: string): Promise<RequestUnder13DeletionResult> => {
      const res = await fetchAuthed('/api/account/under13-deletion', {
        method: 'POST',
        body: JSON.stringify({ athleteUserId }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        approvalId?: string;
        coolingOffDays?: number;
        earliestExecuteAt?: string | null;
        alreadyPending?: boolean;
        error?: string;
        code?: string;
      };
      if (!res.ok || json.success !== true || !json.approvalId) {
        const err = new Error(json.error || `Could not start this request (${res.status})`) as Error & {
          code?: string;
        };
        err.code = json.code;
        throw err;
      }
      await refetch();
      return {
        approvalId: json.approvalId,
        coolingOffDays: json.coolingOffDays ?? 7,
        earliestExecuteAt: json.earliestExecuteAt ?? null,
        alreadyPending: json.alreadyPending === true,
      };
    },
    [fetchAuthed, refetch],
  );

  return {
    enabled, approvals, decided, loading, error, refetch, decide,
    cancelUnder13Deletion, requestUnder13Deletion,
  };
}
